import React from 'react';
import { Box, Typography, Button, Fade, SvgIconProps } from '@mui/material';

interface EmptyStateProps {
    icon: React.ReactElement<SvgIconProps>;
    title: string;
    description?: string;
    actionLabel?: string;
    onAction?: () => void;
}

/**
 * Reusable empty state component for pages with no data.
 *
 * Args:
 *     icon (ReactElement): MUI icon to display.
 *     title (string): Main heading text.
 *     description (string): Optional subtext explaining what to do.
 *     actionLabel (string): Optional button label.
 *     onAction (function): Optional callback for the action button.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
    icon,
    title,
    description,
    actionLabel,
    onAction,
}) => {
    return (
        <Fade in timeout={500}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    px: 3,
                    textAlign: 'center',
                }}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        '& .MuiSvgIcon-root': {
                            fontSize: 40,
                            color: 'text.secondary',
                        },
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="h6" color="text.primary" gutterBottom>
                    {title}
                </Typography>
                {description && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 400, mb: actionLabel ? 3 : 0 }}
                    >
                        {description}
                    </Typography>
                )}
                {actionLabel && onAction && (
                    <Button variant="contained" onClick={onAction} size="large">
                        {actionLabel}
                    </Button>
                )}
            </Box>
        </Fade>
    );
};
